'use client';

import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useState } from 'react';
import { toast, Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import { LazyMapEmbed } from '@/components/ui/LazyMapEmbed';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

interface ContactProps {
  className?: string;
}

export default function Contact({ className }: ContactProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form doğrulama
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Lütfen gerekli alanları doldurun.');
      return;
    }

    // Email doğrulama
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Lütfen geçerli bir email adresi girin.');
      return;
    }

    try {
      // Burada form verilerini işleyebilir veya API'ye gönderebilirsiniz
      console.log('Form data:', formData);
      toast.success('Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.');
      
      // Formu temizle
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const contactItems = [
    {
      icon: MapPin,
      title: 'Adres',
      content: (
        <p className="text-gray-600 text-sm leading-relaxed">
          Maltepe Mh. Davutpaşa Cd. Tim 2 İş Merkezi No:584<br />
          ZEYTİNBURNU/İSTANBUL
        </p>
      ),
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: (
        <a
          href="tel:+902121234567"
          className="text-gray-600 text-sm hover:text-red-800 transition-colors duration-300"
        >
          +90 (212) 123 45 67
        </a>
      ),
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: (
        <a
          href="mailto:filizdekorasyonkozmetik@gmail.com"
          className="text-gray-600 text-sm hover:text-red-800 transition-colors duration-300 break-all"
        >
          filizdekorasyonkozmetik@gmail.com
        </a>
      ),
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: (
        <p className="text-gray-600 text-sm leading-relaxed">
          Pazartesi - Cumartesi: 09:00 - 18:00<br />
          Pazar: Kapalı
        </p>
      ),
    },
  ];

  return (
    <section
      className={cn('relative bg-gradient-to-b from-white to-stone-50/20 overflow-hidden', className)}
    >
      <Toaster position="top-right" richColors />

      {/* Ambient Red Spotlight Glows */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/5 to-transparent rounded-full opacity-30 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 lg:py-28">
        {/* ── Section Header ── */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-4 py-1.5 text-sm font-medium text-red-800 mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            İletişim
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-4">
            Bizimle{' '}
            <span className="text-red-800">
              İletişime Geçin
            </span>
          </h2>

          <p className="text-gray-500 text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
            Sorularınız için bize ulaşın, en kısa sürede size dönüş yapalım.
          </p>
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* ── LEFT: Form Card ── */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10">
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Bize Yazın</h3>
              <p className="text-sm text-gray-500">Formu doldurun, size dönelim.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Ad Soyad */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-600 transition-colors"
                  >
                    Adınız Soyadınız <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Adınız Soyadınız"
                    className="w-full rounded-xl border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm placeholder:text-gray-400 transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-600 transition-colors"
                  >
                    E-posta Adresiniz <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ornek@email.com"
                    className="w-full rounded-xl border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm placeholder:text-gray-400 transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Telefon */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-600 transition-colors"
                  >
                    Telefon Numaranız
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="05XX XXX XX XX"
                    className="w-full rounded-xl border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm placeholder:text-gray-400 transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>

                {/* Konu */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-600 transition-colors"
                  >
                    Konu
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Mesajınızın konusu"
                    className="w-full rounded-xl border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm placeholder:text-gray-400 transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>
              </div>

              {/* Mesaj */}
              <div className="space-y-1.5">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-600 transition-colors"
                >
                  Mesajınız <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Mesajınızı buraya yazın..."
                  className="w-full h-36 rounded-xl border-gray-200 bg-gray-50/50 px-4 py-3 text-sm placeholder:text-gray-400 resize-none transition-all duration-200 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl h-12 text-sm font-semibold tracking-wide shadow-md hover:shadow-lg hover:shadow-red-500/25 transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
              >
                Mesaj Gönder
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {/* Team Photo */}
            <div className="mt-10">
              <div className="relative w-full h-56 sm:h-64 rounded-3xl overflow-hidden group">
                <img
                  src="/images/contact-team.webp"
                  alt="Ekibimiz veya Ofisimiz"
                  className="object-cover w-full h-full transition-transform duration-700 will-change-transform group-hover:scale-105"
                  loading="lazy"
                />
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ── RIGHT: Contact Info + Map ── */}
          <div className="space-y-6">
            {/* Contact Info Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">İletişim Bilgileri</h3>
              <p className="text-sm text-gray-500 mb-8">Bize aşağıdaki kanallardan ulaşabilirsiniz.</p>

              <div className="space-y-4">
                {contactItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 rounded-2xl bg-gray-50/80 p-5 transition-colors duration-200 hover:bg-gray-100/80"
                    >
                      {/* Gradient icon circle */}
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center text-white shadow-sm shadow-red-500/20">
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                      </div>

                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h4>
                        {item.content}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Map */}
            <div className="overflow-hidden rounded-3xl bg-white shadow-sm border border-gray-100">
              <LazyMapEmbed
                title="Filiz Kozmetik mağaza konumu — Google Haritalar"
                height={300}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}